import { useCallback, useMemo, useState } from 'react';
import { Button, Select, Space, Table, Tag, Typography } from 'antd';
import type { TablePaginationConfig } from 'antd/es/table/interface';
import { Link, useNavigate, type NavigateFunction } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';

import {
  useMedicalEmployeesInfinite,
  MEDICAL_EMPLOYEES_PAGE_SIZE,
  type MedicalEmployeesListFilters,
} from '@/entities/medical/api/medicalChecksApi';
import {
  checkTypeLabels,
  checkStatusLabels,
  type MedicalCheckEmployeeRow,
  type MedicalCheckRow,
  type CheckType,
  type CheckStatus,
} from '@/entities/medical/model/medicalChecksSchema';
import {
  medicalCheckStatusTagColor,
  medicalAdmissionTagColor,
  medicalDecisionOrAdmissionLabel,
} from '@/entities/medical/model/medicalCheckTableTags';
import { antTableScroll } from '@/shared/constants/antTable';
import { SearchInput } from '@/shared/ui/SearchInput';
import { OrgTreeDrawer } from '@/pages/Employees/OrgTreeDrawer';
import { OrgStructureSelectDrawer } from '@/shared/ui/OrgStructureSelectDrawer';
import sharedExpanded from '@/shared/ui/ExpandedRows/ExpandedRows.module.scss';

import s from './MedRecordsTab.module.scss';
import { ApartmentOutlined } from '@ant-design/icons';

const { Text } = Typography;

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'not_assigned', label: 'Не назначен' },
  { value: 'assigned', label: 'Назначен' },
  { value: 'in_progress', label: 'В процессе' },
  { value: 'passed', label: 'Пройден' },
  { value: 'overdue', label: 'Просрочен' },
  { value: 'not_passed', label: 'Не пройден' },
];

const CHECK_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'Все типы' },
  { value: 'preliminary', label: 'Предварительный' },
  { value: 'periodic', label: 'Периодический' },
  { value: 'extraordinary', label: 'Внеочередной' },
  { value: 'pre_shift', label: 'Предсменный' },
  { value: 'post_shift', label: 'Послесменный' },
];

const QUICK_FILTERS = [
  { label: 'Просроченные', value: 'overdue' as const },
  { label: 'Недопущенные', value: 'not_allowed' as const },
];

type QuickKey = (typeof QUICK_FILTERS)[number]['value'] | '';

export type MedRecordsTabProps = {
  problemEmployeesOnly?: boolean;
};

function ExpandedChecks({ checks }: { checks: MedicalCheckRow[] }) {
  if (!checks.length) return <Text type="secondary">Нет записей о медосмотрах</Text>;

  return (
    <div className={sharedExpanded.expanded}>
      <Text className={sharedExpanded.hint}>Медосмотры сотрудника:</Text>
      {checks.map((check) => (
        <Link
          key={check.id}
          to={`/medical/records/${check.id}`}
          className={`${sharedExpanded.row} ${s['root__expanded-link']}`}
        >
          <span className={s['root__expanded-type']}>
            {checkTypeLabels[check.check_type as CheckType] ?? check.check_type}
          </span>
          <span className={sharedExpanded.meta}>
            {check.checked_at ? `Дата: ${check.checked_at}` : ''}
            {check.next_check_date ? ` · Следующий: ${check.next_check_date}` : ''}
            {check.conclusion_summary ? ` · Заключение: ${check.conclusion_summary}` : ''}
            {check.restrictions ? ` · Ограничения: ${check.restrictions}` : ''}
          </span>
          <Tag color={medicalCheckStatusTagColor[check.status] ?? 'default'}>
            {checkStatusLabels[check.status as CheckStatus] ?? check.status}
          </Tag>
          {check.decision ? (
            <Tag color={medicalAdmissionTagColor[check.decision] ?? 'default'}>
              {medicalDecisionOrAdmissionLabel(check.decision)}
            </Tag>
          ) : null}
        </Link>
      ))}
    </div>
  );
}

type EmployeesTableProps = {
  filters: MedicalEmployeesListFilters;
  navigate: NavigateFunction;
};

function EmployeesTable({ filters, navigate }: EmployeesTableProps) {
  const { data, isLoading, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useMedicalEmployeesInfinite(filters);

  const [pageIndex, setPageIndex] = useState(0);
  const loadedPageCount = data?.pages.length ?? 0;
  const maxLoadedIndex = Math.max(0, loadedPageCount - 1);
  const viewIndex = Math.min(pageIndex, maxLoadedIndex);

  const employees = useMemo(() => data?.pages[viewIndex]?.items ?? [], [data?.pages, viewIndex]);
  const itemsOnPage = employees.length;
  const canGoForward = viewIndex < loadedPageCount - 1 || Boolean(hasNextPage);
  const paginationTotal = canGoForward
    ? (viewIndex + 1) * MEDICAL_EMPLOYEES_PAGE_SIZE + 1
    : viewIndex * MEDICAL_EMPLOYEES_PAGE_SIZE + itemsOnPage;

  const showPagination = !(isLoading && loadedPageCount === 0) && (itemsOnPage > 0 || canGoForward);

  const handleTableChange = useCallback(
    async (pag: TablePaginationConfig) => {
      const idx = (pag.current ?? 1) - 1;
      if (idx < 0 || idx === viewIndex) return;
      if (idx < viewIndex) {
        setPageIndex(idx);
        return;
      }
      if (idx < loadedPageCount) {
        setPageIndex(idx);
        return;
      }
      if (idx === loadedPageCount && hasNextPage && !isFetchingNextPage) {
        await fetchNextPage();
        setPageIndex(idx);
      }
    },
    [viewIndex, loadedPageCount, hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const columns: ColumnsType<MedicalCheckEmployeeRow> = [
    {
      title: 'ФИО',
      dataIndex: 'full_name',
      key: 'name',
      render: (name: string) => <span className={s['root__link-cell']}>{name}</span>,
    },
    {
      title: 'Табельный №',
      dataIndex: 'tab_number',
      key: 'tab',
      width: 120,
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Должность',
      key: 'pos',
      render: (_: unknown, row: MedicalCheckEmployeeRow) => row.position?.name ?? '—',
    },
    {
      title: 'Подразделение',
      key: 'dept',
      render: (_: unknown, row: MedicalCheckEmployeeRow) => row.org_unit.name,
    },
    {
      title: 'Допуск',
      dataIndex: 'current_admission_status',
      key: 'admission',
      width: 150,
      render: (val: string) => (
        <Tag color={medicalAdmissionTagColor[val] ?? 'default'}>{medicalDecisionOrAdmissionLabel(val)}</Tag>
      ),
    },
    {
      title: 'Статус медосмотра',
      key: 'status',
      width: 170,
      render: (_: unknown, row: MedicalCheckEmployeeRow) => {
        const check = row.current_check;
        if (!check) return <Tag>—</Tag>;
        return (
          <Tag color={medicalCheckStatusTagColor[check.status] ?? 'default'}>
            {checkStatusLabels[check.status as CheckStatus] ?? check.status}
          </Tag>
        );
      },
    },
    {
      title: 'Тип',
      key: 'type',
      width: 150,
      render: (_: unknown, row: MedicalCheckEmployeeRow) => {
        const check = row.current_check;
        if (!check) return '—';
        return checkTypeLabels[check.check_type as CheckType] ?? check.check_type;
      },
    },
    {
      title: 'Следующий',
      key: 'next',
      width: 120,
      render: (_: unknown, row: MedicalCheckEmployeeRow) => row.current_check?.next_check_date ?? '—',
    },
  ];

  const listLoading = isLoading || (isFetching && !isFetchingNextPage);

  return (
    <Table
      loading={listLoading}
      dataSource={employees}
      columns={columns}
      rowKey="id"
      size="small"
      pagination={
        showPagination
          ? {
              current: viewIndex + 1,
              pageSize: MEDICAL_EMPLOYEES_PAGE_SIZE,
              total: paginationTotal,
              showSizeChanger: false,
              showTotal: (_total, range) => `${range[0]}–${range[1]}`,
              disabled: isFetchingNextPage,
            }
          : false
      }
      onChange={handleTableChange}
      scroll={antTableScroll}
      expandable={{
        expandedRowRender: (row: MedicalCheckEmployeeRow) => <ExpandedChecks checks={row.recent_checks} />,
      }}
      onRow={(record) => ({
        onClick: (e) => {
          const target = e.target as HTMLElement | null;
          if (!target) return;
          if (target.closest('button, a, .ant-table-row-expand-icon, .ant-table-expand-icon')) return;
          navigate(`/medical/employee/${record.id}`);
        },
        className: s['root__clickable-row'],
      })}
    />
  );
}

export function MedRecordsTab({ problemEmployeesOnly }: MedRecordsTabProps) {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [checkTypeFilter, setCheckTypeFilter] = useState('');
  const [quickFilter, setQuickFilter] = useState<QuickKey>(problemEmployeesOnly ? 'not_allowed' : '');
  const [orgDrawerOpen, setOrgDrawerOpen] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState<{ id: string; name: string } | null>(null);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    const trimmed = val.trim();
    if (trimmed.length >= 2 || trimmed.length === 0) {
      setDebouncedSearch(trimmed.length >= 2 ? trimmed : '');
    }
  }, []);

  const filters: MedicalEmployeesListFilters = useMemo(() => {
    const p: MedicalEmployeesListFilters = {};
    if (debouncedSearch) p.q = debouncedSearch;
    if (departmentFilter) p.orgUnitId = departmentFilter.id;
    if (checkTypeFilter) p.checkTypes = [checkTypeFilter];
    if (statusFilter) p.statuses = [statusFilter];
    const qf = problemEmployeesOnly ? 'not_allowed' : quickFilter;
    if (qf) p.preset = qf;
    return p;
  }, [debouncedSearch, departmentFilter, checkTypeFilter, statusFilter, quickFilter, problemEmployeesOnly]);

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);

  const handleOrgSelect = (id: string, name: string, opts?: { keepDrawerOpen?: boolean }) => {
    setDepartmentFilter({ id, name });
    if (!opts?.keepDrawerOpen) {
      setOrgDrawerOpen(false);
    }
  };

  const handleQuickClick = (key: QuickKey) => {
    if (problemEmployeesOnly) return;
    setQuickFilter((prev) => (prev === key ? '' : key));
  };

  return (
    <>
      {problemEmployeesOnly ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
          Сотрудники с проблемами по медосмотрам (просроченные / недопущенные).
        </Text>
      ) : null}

      <Space wrap className={s.root__filters}>
        <SearchInput
          placeholder="Поиск по ФИО, табельному №..."
          className={s['root__filter-input']}
          value={search}
          onChange={handleSearchChange}
          allowClear
        />
        <Select
          className={s['root__filter-select-status']}
          value={checkTypeFilter}
          onChange={(v) => setCheckTypeFilter(v)}
          options={CHECK_TYPE_FILTER_OPTIONS}
        />
        <Select
          className={s['root__filter-select-status']}
          value={statusFilter}
          disabled={problemEmployeesOnly}
          onChange={(v) => {
            setStatusFilter(v);
            setQuickFilter('');
          }}
          options={STATUS_FILTER_OPTIONS}
        />
        <Button icon={<ApartmentOutlined size={16} />} onClick={() => setOrgDrawerOpen(true)}>
          Подразделение
        </Button>
      </Space>

      {departmentFilter ? (
        <div className={s['root__active-tag']}>
          <Text type="secondary" className={s['root__tag-label']}>
            Подразделение:
          </Text>
          <Tag closable onClose={() => setDepartmentFilter(null)}>
            {departmentFilter.name}
          </Tag>
        </div>
      ) : null}

      {!problemEmployeesOnly ? (
        <Space wrap className={s.root__presets}>
          {QUICK_FILTERS.map((p) => (
            <Tag
              key={p.value}
              className={`${s.root__preset} ${quickFilter === p.value ? s.root__preset_active : ''}`}
              onClick={() => handleQuickClick(p.value)}
            >
              {p.label}
            </Tag>
          ))}
        </Space>
      ) : null}

      <EmployeesTable key={filtersKey} filters={filters} navigate={navigate} />

      <OrgStructureSelectDrawer open={orgDrawerOpen} onClose={() => setOrgDrawerOpen(false)} width={420}>
        <OrgTreeDrawer onSelect={handleOrgSelect} />
      </OrgStructureSelectDrawer>
    </>
  );
}
